import { isEqual } from 'lodash';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { trackSegment } from '~/components/SegmentAnalytics';
import { Charts } from '~/components/Shared/Charts/Charts';
import {
  BuildQueryParams,
  SearchQuery,
} from '~/features/advanced-search/types';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { Dashboard } from '~/redux/api/methods/dashboardV2';
import { SearchParams } from '~/redux/api/methods/evisearch';
import { slices } from '~/redux/slices';
import { Chart } from '~/types/DashboardV2';

import { toFilter } from '../DashboardV2.utils';

interface DashboardV2ChartsProps {
  charts: Chart[];
  queryParams: BuildQueryParams;
  query?: SearchQuery;
  entity?: Dashboard['entity'];
  searchParams?: SearchParams;
}

export const DashboardV2Charts = ({
  entity,
  charts,
  queryParams,
  query,
  searchParams,
}: DashboardV2ChartsProps) => {
  const dispatch = useDispatch();

  const currentChart = useSelector(
    slices.dashboardV2.selectors.selectCurrentChart,
  );
  const dashboard = useSelector(
    slices.dashboardV2.selectors.selectActiveDashboard,
  );
  const intervals = useSelector(slices.dashboardV2.selectors.selectIntervals);
  const chartWidths = useSelector(
    slices.dashboardV2.selectors.selectChartWidths,
  );

  const user = useCurrentUser();

  const onRearrangeCharts = useCallback(
    (updatedFilters: Chart[], activeFilter?: Chart) => {
      const currentOrder = charts.map((chart) => chart.id);
      const newOrder = updatedFilters.map((chart) => chart.id);
      if (!isEqual(currentOrder, newOrder)) {
        trackSegment('selectReorderChart', {
          id: dashboard.id,
          name: dashboard.name,
          value: activeFilter?.label,
          user_id: user.id,
          client_id: user.client,
        });

        dispatch(slices.dashboardV2.actions.setDashboardDirty(true));
      }
      dispatch(slices.dashboardV2.actions.setChartFilters(updatedFilters));
    },
    [charts],
  );

  function onChartWidthChange(filter: Chart, newWidth: string) {
    dispatch(
      slices.dashboardV2.actions.setChartWidth({
        id: filter.id,
        value: newWidth,
      }),
    );
  }

  function onChartTypeChange(filter: Chart, newType: string) {
    dispatch(
      slices.dashboardV2.actions.setChartType({
        id: filter.id,
        value: newType,
      }),
    );
  }

  return (
    <Charts
      intervals={intervals}
      chartWidths={chartWidths}
      searchParams={searchParams}
      onIntervalChange={(action, filter) => {
        trackSegment('selectCustomInterval', {
          filter_id: filter.id,
          name: filter.label,
          value: action.value,
          dashboard_id: dashboard.id,
          dashboard_name: dashboard.name,
          user_id: user.id,
          client_id: user.client,
        });
        dispatch(
          slices.dashboardV2.actions.setChartInterval({
            id: filter.id,
            value: action.value,
          }),
        );
      }}
      onRemoveItem={(searchFilter) => {
        trackSegment('selectDeleteCharts', {
          id: dashboard.id,
          name: dashboard.name,
          value: searchFilter.label,
          user_id: user.id,
          client_id: user.client,
        });

        dispatch(slices.dashboardV2.actions.deleteChart(searchFilter));
      }}
      onRearrangeCharts={onRearrangeCharts}
      onChartWidthChange={onChartWidthChange}
      onChartTypeChange={onChartTypeChange}
      charts={charts}
      onCrossFiltersChange={(crossFilters, fieldId) => {
        const newFilters = crossFilters.map((crossFilter) => {
          return toFilter(
            crossFilter.fieldId,
            crossFilter.value!,
            crossFilter.type,
          );
        });
        dispatch(slices.dashboardV2.actions.setCrossFilters(newFilters));

        dispatch(slices.dashboardV2.actions.setCurrentChart(fieldId));
      }}
      queryParams={queryParams}
      query={query}
      currentChart={currentChart}
      entity={entity}
    ></Charts>
  );
};
